<template>
  <div class="basic-select-input-container">
    <Field
      v-slot="{ field }"
      v-model="computedMutatedValue"
      :label="label"
      :name="name"
      :rules="validation"
    >
      <div class="basic-select-input-container-main">
        <label v-if="displayLabel">{{ label }}</label>
        <div 
          class="basic-select-input-content bordered-bottom"
          :class="{'autocompleted': valueHasBeenAutocompleted}"
        >
          <v-select
            :ref="name"
            v-model="computedMutatedValue"
            class="custom-select-style"
            v-bind="field"
            :placeholder="placeholder"
            :options="computedMutatedDataOptions"
            append-to-body
            :calculate-position="calculatePosition"
            :disabled="isReadOnly || valueHasBeenAutocompleted"
            label="formGeneratorLabel"
            :get-option-key="getOptionKey"
            :clearable="!valueHasBeenAutocompleted"
            @option:selected="emitData"
            @open="openSelect()"
          >
            <template #option="option">
              <p>{{ buildLabel(option) }}</p>
            </template>
            <template #no-options="{ searching }">
              <template v-if="searching">
                {{ $t('errors.datas_not_found') }}
              </template>
            </template>
            <template #spinner>
              <img
                v-if="loading"
                style="width: 1rem; height: 1rem; margin-left: 5px"
                src="@/assets/icons/spinner-dots.svg"
                alt="loader"
              >
            </template>
            <template #open-indicator>
              <img
                style="width: 1rem; height: 1rem"
                src="@/assets/icons/arrow-down.svg"
                alt="display options"
              >
            </template>
          </v-select>
          <transition name="slideleft">
            <FullScreenSelect
              v-if="showSelectorFS"
              :defaultValue="computedMutatedValue"
              :options="computedMutatedDataOptions"
              :name="label"
              :labelFields="labelFields"
              :searchbar="true"
              @fs-select-close="showSelectorFS = false"
              @selected-option="onChange"
            />
          </transition>
        </div>
      </div>
      <ErrorMessage
        class="error-msg"
        :name="name"
      />
    </Field>
  </div>
</template>

<script>
import FullScreenSelect from '@/components/GenericComponents/Form/FullScreenSelect.vue'
import { appConfigurationStore } from '@/stores/appConfigurationStore.js'
import { configurationService } from '@/services/configuration.service'
import { mapState } from 'pinia'
import { store } from '@/stores/index.js'
import { Field, ErrorMessage } from 'vee-validate'
import { findBestMatch } from 'string-similarity'
import { EventBus } from '@/event-bus'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

/*
 * Props:
 * placeholder: optional, only if u want a disabled/selected option to be displayed as title
 * name: input name (string)
 * value: the value of the option that need to be displayed first (Number) --!! if you want a placeholder to be set this need to be undefined !!--
 * dataOptions:
 *      [
 *      {
 *        value: value of the option([String, Number]),
 *        label: label displayed of the selected (string)
 *      }
 *      ]
 */
export default {
  name: 'BasicSelect',
  components: {
    Field,
    ErrorMessage,
    FullScreenSelect,
    vSelect
  },
  props: {
    label: String,
    validation: [String, Boolean],
    placeholder: [String, Number],
    defaultValue: {
      type: [String, Array, Object, Boolean],
      default: undefined
    },
    name: String,
    dataOptions: Array,
    apiUrl: String,
    uniqueApiCall: {
      type: Boolean,
      default: false
    },
    labelFields: Array,
    translate: Boolean,
    displayLabel: {
      type: Boolean,
      default: false
    },
    getChildByName: {
      type: Function,
      default: null
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    selectPlaceholder: {
      type: Boolean,
      default: false
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedValue: this.checkDefaultValue(this.defaultValue)
        ? this.checkDefaultValue(this.defaultValue)
        : this.defaultValue,
      mutatedDataOptions: this.dataOptions,
      url: null,
      autocomplete_: {},
      showSelectorFS: false,
      loading: false,
      valueHasBeenAutocompleted: false
    }
  },
  computed: {
    ...mapState(store, ['formApiResponses', 'deviceType']),
    ...mapState(appConfigurationStore, ['appConfiguration']),
    computedMutatedValue: {
      get() {
        if (this.mutatedValue) {
          if (typeof this.mutatedValue === 'object') {
            return {
              ...this.mutatedValue,
              formGeneratorLabel: this.buildLabel(this.mutatedValue)
            }
          } else {
            let valueFound
            for (let index = 0; index < this.computedMutatedDataOptions.length; index++) {
              const element = this.computedMutatedDataOptions[index]?.formGeneratorLabel
              if(element.toLowerCase() === this.mutatedValue.toLowerCase()) {
                valueFound = element
                break
              }
            }
            return valueFound
          }
        } else {
          return undefined
        }
      },
      set(value) {
        let newValue = value ? JSON.parse(JSON.stringify(value)) : undefined
        if (newValue && typeof newValue === 'object') {
          delete newValue.formGeneratorLabel
        }
        this.mutatedValue = newValue
        this.emitData()
      }
    },
    computedMutatedDataOptions() {
      if (this.mutatedDataOptions && Array.isArray(this.mutatedDataOptions)) {
        return this.mutatedDataOptions.map(option => {
          return {
            ...option,
            formGeneratorLabel: this.buildLabel(option)
          }
        })
      } else {
        return []
      }
    },
    isCapencyEnable() {
      return this.$appConfigurationStore.appConfiguration?.modules?.general?.autocomplete?.validation?.enabled && this.$appConfigurationStore.appConfiguration?.modules?.general?.autocomplete?.validation?.providerName == 'capency'
    }
  },
  watch: {
    url(newUrl, oldUrl) {
      if (newUrl !== oldUrl) {
        this.populateAPIData()
      }
    },
    autocomplete_(value) {
      if (value.list && value.list.length > 0) {
        const bestMatch = findBestMatch(
          value.list[0].toLowerCase(),
          this.mutatedDataOptions.map(o => this.buildLabel(o).toLowerCase())
        )

        if (bestMatch) {
          this.mutatedValue = this.mutatedDataOptions[bestMatch.bestMatchIndex]
        }
      }
    },
    defaultValue() {
      if (this.mutatedValue === undefined) {
        this.mutatedValue = this.defaultValue
        if (this.apiUrl && this.mutatedValue) {
          this.$emit('addData', { value: JSON.parse(JSON.stringify(this.mutatedValue)), name: this.name })
          EventBus.emit('formGeneratorValueChanged', {
            name: this.name,
            value: this.mutatedValue
          })
        }
      }
    }
  },
  mounted() {
    if (this.apiUrl) {
      this.buildAPIURL()
      EventBus.on('formGeneratorValueChanged', () => {
        this.buildAPIURL()
      })
    }
    this.emitData()
    if(this.isCapencyEnable) {
      EventBus.on('changeValue' + this.name, value => {
        if(value == '') {
          this.valueHasBeenAutocompleted = false
        } else {
          this.valueHasBeenAutocompleted = true
        }
        this.computedMutatedValue = value
      })
    }

  },
  beforeUnmount() {
    EventBus.off('formGeneratorValueChanged')
    if(this.isCapencyEnable) {
      EventBus.off('changeValue' + this.name)
    }
  },
  methods: {
    buildAPIURL() {
      let url = this.apiUrl
      const findVars = this.apiUrl.match(/{[^{]*}/g)
      if (findVars) {
        findVars.forEach(variable => {
          const value = this.getChildByName(variable.slice(1, -1))
          if (value) {
            url = url.replace(variable, value)
            this.url = url
          }
        })
      } else {
        this.url = url
      }
    },
    async populateAPIData() {
      let urlFormatted = this.url.replaceAll('/', '_')
      if (urlFormatted.includes('?')) {
        urlFormatted = urlFormatted.split('?')[0]
      }
      if (!this.uniqueApiCall || !this.formApiResponses[urlFormatted]) {
        this.loading = true
        // if uniqueApiCall is false, do the call everytime
        this.mutatedDataOptions = await configurationService.getFormData(this.url, null)
        let data = {
          key: urlFormatted,
          value: this.mutatedDataOptions
        }
        await this.$store.setFormApiResponses(data)
        this.loading = false
      } else {
        // set the mutatedDataOption with data set in store
        this.mutatedDataOptions = this.formApiResponses[urlFormatted]
      }

      if (this.mutatedValue?.id && this.mutatedDataOptions) {
        this.mutatedValue = this.mutatedDataOptions.find(option => option.id == this.mutatedValue.id)
      } else if (this.placeholder === undefined && this.mutatedDataOptions && this.mutatedDataOptions.length > 0) {
        this.mutatedValue = this.mutatedDataOptions[0]
      } else if (this.apiUrl && this.mutatedDataOptions && this.mutatedDataOptions.length == 0) {
        this.mutatedValue = undefined
      } else if (this.defaultValue) {
        this.mutatedValue = this.defaultValue
      } else {
        this.mutatedValue = undefined
      }
      this.emitData()
    },
    emitData() {
      if (this.mutatedValue !== undefined && this.mutatedValue !== null) {
        this.$emit('addData', { value: JSON.parse(JSON.stringify(this.mutatedValue)), name: this.name })
        if (this.apiUrl) {
          EventBus.emit('formGeneratorValueChanged', {
            name: this.name,
            value: this.mutatedValue
          })
        }
      } else {
        this.$emit('addData', { value: '', name: this.name })
      }
    },
    buildLabel(option) {
      if (this.translate) {
        if (option.label) {
          return this.$te('form.' + option.label) ? this.$t('form.' + option.label) : option.label
        }
        return this.$t('form.' + option[this.labelFields[0]])
      }
      if (!this.apiUrl) {
        return this.$te('form.' + option.label) ? this.$t('form.' + option.label) : option.label
      }
      if (!this.labelFields) {
        return option.label
      }
      return this.labelFields.map(field => option[field]).join(' ')
    },
    getOptionKey(option) {
      try {
        return JSON.stringify(option)
      } catch (e) {
        if (typeof option === 'object' && option.id) {
          return option.id
        }
        return null
      }
    },
    checkDefaultValue(defaultValue) {
      if (this.dataOptions) {
        for (const option of this.dataOptions) {
          if (option.value !== null && option.value === defaultValue) {
            return option
          }
        }
      }
      return null
    },
    calculatePosition(dropdownList, component, dimensions) {
      dropdownList.style.top =
        (window.pageYOffset + component.$el.getBoundingClientRect().top + component.$el.clientHeight).toString() +
        'px'
      dropdownList.style.left = dimensions.left
      dropdownList.style.width = dimensions.width
    },
    onChange(value) {
      this.mutatedValue = value
      this.emitData()
    },
    openSelect() {
      if (this.deviceType !== 'desktop') {
        switch (this.$attrs.formAction) {
          case 'createDynamicList':
            return
          default:
            document.activeElement.blur()
            this.showSelectorFS = true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .basic-select-input-container {
    margin: 10px 0;

    .basic-select-input-container-main {
      text-align: left;

      label {
        padding-left: 12px;
        color: #777;
        font-size: 0.75rem !important;
        font-weight: bold;
      }
    }

    .basic-select-input-content {
      width: 100%;
      white-space: nowrap;
      align-items: center;
      padding-left: 0.5rem;
    }
  }
</style>

<style lang="scss">
  .basic-select-input-container {
    .custom-select-style input {
      height: 100%;
      border: none;
    }

    .vs__dropdown-toggle {
      display: flex;
      align-items: center;
    }

    .vs__selected-options {
      margin-right: 5px;
    }

    .basic-select-input-content.autocompleted {
      border-bottom: 1px #777 dotted;

      .custom-select-style * {
        padding: 0 0 1px !important;
        border: none !important;
        background-color: white;
        color: #777;
      }
    }

    .custom-select-style * {
      padding: 0 0 1px !important;
      border: none !important;
    }

    .custom-select-style .vs__clear {
      fill: transparent;
      background-image: url('@/assets/icons/cancel.svg');
      background-position: center right;
      background-origin: content-box;
      background-repeat: no-repeat;
      background-size: contain;
      width: 1rem;
      height: 1rem;
      margin-right: 8px !important;
    }
  }
</style>
